import { useEffect, useState } from "react";
import { Btn, Card, Icon, Pill, Toggle, T, tx } from "@v0y4ge/jrni-ui";
import { useNav } from "../../shell/router";
import { Screen, PageHeader } from "../../shell/chrome";
import { CreationStore } from "../events/creation/store";
import { useEvents } from "../events/useEvents";
import { toast } from "../../shell/toast";
import { useTemplates, upsertTemplate, newTemplateId } from "./useTemplates";
import { creation, duplicateEventTemplate } from "@v0y4ge/api-client";
import { useT, t as i18nT } from "../../lib/i18n";

export default function TemplateDetail() {
  const translate = useT();
  const nav = useNav();
  const { templates } = useTemplates();
  const { events } = useEvents();
  const id = (nav.params.id as string) || templates[0]?.id;
  const t = templates.find((x) => x.id === id) || templates[0];
  const [name, setName] = useState(t?.name || "");
  useEffect(() => { if (t) setName(t.name); }, [t?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!t) return <Screen rail="events"><div style={{ padding: 40, color: T.soft }}>{translate("templates.detail.templateNotFound", "Template not found.")}</div></Screen>;

  const saveName = async () => {
    const nm = name.trim();
    if (!t || !nm || nm === t.name) return;
    try {
      await creation.updateEventTemplate(t.id, { name: nm } as any);
      upsertTemplate({ ...t, name: nm, lastEdit: "Just now" });
    } catch (e) {
      console.warn("updateEventTemplate failed:", e);
      setName(t.name);
      toast({ tone: "danger", title: translate("templates.detail.couldnTRenameTemplate", "Couldn't rename template"), subtitle: translate("templates.detail.retryInAMoment", "Retry in a moment.") });
    }
  };
  const use = () => { CreationStore.setType(t.typeId || t.id); nav.go("create.basics", { templateId: t.id }); };
  const duplicate = async () => {
    try {
      const dto: any = await duplicateEventTemplate(t.id);
      const copy = { ...t, id: dto?.id ?? newTemplateId(), name: dto?.name ?? i18nT("templates.detail.nameTNameCopy.icu", { value0: name || t.name }), usage: 0, lastEdit: "Just now", status: "draft" as const };
      upsertTemplate(copy);
      toast({ tone: "success", title: translate("templates.detail.templateDuplicated", "Template duplicated"), subtitle: i18nT("templates.detail.aCopyOfNameTNameWasAddedAsADraft.icu", { value0: name || t.name }) });
      nav.go("templates.detail", { id: copy.id });
    } catch (e) {
      console.warn("duplicateEventTemplate failed:", e);
      toast({ tone: "danger", title: translate("templates.detail.couldnTDuplicateTemplate", "Couldn't duplicate template"), subtitle: translate("templates.detail.noCopyWasCreatedRetryInAMoment", "No copy was created. Retry in a moment.") });
    }
  };

  return (
    <Screen rail="events">
      <PageHeader
        title={<input value={name} placeholder={translate("templates.detail.untitledTemplate", "Untitled template")} aria-label={translate("templates.detail.templateName", "Template name")}
          size={Math.max(24, name.length + 2)}
          onChange={(e) => setName(e.target.value)} onBlur={saveName}
          style={{ font: "inherit", color: "inherit", border: "none", borderBottom: `1px dashed ${T.n4}`, background: "transparent", outline: "none", padding: "0 2px 2px", maxWidth: "100%", fontFamily: T.font }} />}
        subtitle={i18nT("templates.detail.tTypeTUsageEventsCreatedFromThisTemplateLastEditedT.icu", { value0: t.type, value1: t.usage, value2: t.lastEdit })}
        breadcrumb={<>
          <span style={{ cursor: "pointer" }} onClick={() => nav.go("dashboard")}>{translate("templates.detail.dashboard", "Dashboard")}</span>
          <Icon.chev size={10} dir="right" />
          <span style={{ cursor: "pointer" }} onClick={() => nav.go("events")}>{translate("templates.detail.events", "Events")}</span>
          <Icon.chev size={10} dir="right" />
          <span style={{ cursor: "pointer" }} onClick={() => nav.go("templates")}>{translate("templates.detail.templates", "Templates")}</span>
          <Icon.chev size={10} dir="right" />
          <span style={{ color: T.n1 }}>{name || t.name}</span>
        </>}
        actions={<>
          <Btn variant="secondary" icon={<Icon.copy size={14} />} onClick={() => void duplicate()}>{translate("templates.detail.duplicate", "Duplicate")}</Btn>
          <Btn variant="secondary" icon={<Icon.edit size={14} />} onClick={use}>{translate("templates.detail.editTemplate", "Edit template")}</Btn>
          <Btn icon={<Icon.plus size={14} />} onClick={use}>{translate("templates.detail.useThisTemplate", "Use this template")}</Btn>
        </>}
      />

      <div style={{ flex: 1, overflow: "auto", padding: "16px 24px 24px", display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 16 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <Card style={{ padding: 0, overflow: "hidden" }}>
            <div style={{ height: 200, background: `linear-gradient(135deg, ${t.hue}, ${T.p1})`, padding: 32, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <Pill tone="primary" style={{ background: "rgba(255,255,255,0.25)", color: T.white }}>{t.type}</Pill>
                <div style={{ display: "flex", gap: 6 }}>{t.tags.map((tag) => <Pill key={tag} style={{ background: "rgba(255,255,255,0.2)", color: T.white }}>{tag}</Pill>)}</div>
              </div>
              <div>
                <div style={{ fontSize: 28, fontWeight: 800, color: T.white, letterSpacing: -0.5, lineHeight: 1.1 }}>{name || t.name}</div>
                <div style={{ fontSize: 14, color: "rgba(255,255,255,0.85)", marginTop: 6, maxWidth: 540 }}>{t.desc}</div>
              </div>
            </div>
          </Card>

          <Card>
            <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.border}` }}><h3 style={{ ...tx.h3, fontWeight: 600 }}>{translate("templates.detail.whatSIncluded", "What's included")}</h3></div>
            <div style={{ padding: 20, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              {[
                { l: "Default capacity", v: `${t.defaults.capacity} seats`, i: <Icon.users size={14} /> },
                { l: "Registration window", v: "30 days before — 2h before", i: <Icon.clock size={14} /> },
                { l: "Ticket types", v: `${t.defaults.tickets} configured`, i: <Icon.ticket size={14} /> },
                { l: "Email & SMS templates", v: `${t.defaults.comms} comms wired`, i: <Icon.mail size={14} /> },
                { l: "Booking questions", v: "4 standard + 1 conditional", i: <Icon.help size={14} /> },
                { l: "Landing page theme", v: "Brand · gradient hero", i: <Icon.browser size={14} /> },
              ].map((row, i) => (
                <div key={i} style={{ display: "flex", gap: 10, padding: 12, border: `1px solid ${T.border}`, borderRadius: 6 }}>
                  <span style={{ width: 32, height: 32, borderRadius: 6, background: T.p3, color: T.p1, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{row.i}</span>
                  <div>
                    <div style={{ fontSize: 12, color: T.soft, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.04 }}>{row.l}</div>
                    <div style={{ fontSize: 13, color: T.n1, fontWeight: 500, marginTop: 2 }}>{row.v}</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <Card>
            <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.border}` }}><h3 style={{ ...tx.h3, fontWeight: 600 }}>{translate("templates.detail.recentEventsUsingThisTemplate", "Recent events using this template")}</h3></div>
            <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
              {events.slice(0, Math.min(t.usage, 4)).map((ev, i) => (
                <li key={ev.id} onClick={() => nav.openEvent(ev)} style={{ padding: "12px 16px", display: "flex", gap: 12, alignItems: "center", borderTop: i ? `1px solid ${T.border}` : "none", cursor: "pointer" }}>
                  <span style={{ width: 32, height: 32, borderRadius: 6, background: ev.hue, color: T.white, fontSize: 12, fontWeight: 700, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>{ev.title.split(" ").map((w) => w[0]).slice(0, 2).join("")}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, color: T.n1, fontWeight: 600 }}>{ev.title}</div>
                    <div style={{ fontSize: 12, color: T.soft }}>{ev.date} · {ev.host} · {ev.registered}/{ev.capacity} {translate("templates.detail.registered", "registered")}</div>
                  </div>
                  <Icon.arrow size={14} style={{ color: T.soft }} />
                </li>
              ))}
            </ul>
          </Card>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <Card style={{ padding: 20, display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={tx.label}>{translate("templates.detail.performance", "Performance")}</div>
            {[["Avg. fill rate", "92%"], ["Avg. NPS", "58"], ["Avg. revenue", "$3,420"], ["Used by", "4 team members"]].map(([l, v]) => (
              <div key={l} style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontSize: 13, color: T.soft }}>{l}</span>
                <span style={{ fontSize: 13, color: T.n1, fontWeight: 600 }}>{v}</span>
              </div>
            ))}
          </Card>
          <Card style={{ padding: 20 }}>
            <div style={tx.label}>{translate("templates.detail.permissions", "Permissions")}</div>
            <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 8 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <Toggle on />
                <div>
                  <div style={{ fontSize: 13, color: T.n1, fontWeight: 500 }}>{translate("templates.detail.anyoneInWorkspace", "Anyone in workspace")}</div>
                  <div style={{ fontSize: 11, color: T.soft }}>{translate("templates.detail.canUseThisTemplateToCreateEvents", "Can use this template to create events")}</div>
                </div>
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <Toggle />
                <div>
                  <div style={{ fontSize: 13, color: T.n1, fontWeight: 500 }}>{translate("templates.detail.subAccountsCanUse", "Sub-accounts can use")}</div>
                  <div style={{ fontSize: 11, color: T.soft }}>{translate("templates.detail.shareToAcmeNycAndLondon", "Share to Acme · NYC and London")}</div>
                </div>
              </label>
            </div>
          </Card>
        </div>
      </div>
    </Screen>
  );
}
